import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { PriceLookupMaps } from "@/query/hooks/useGlobalPrices";

interface PricingCacheContextType {
  priceMaps: PriceLookupMaps;
}

const PricingCacheContext = createContext<PricingCacheContextType | null>(null);

export function PricingCacheProvider({
  children,
  priceMaps,
}: {
  children: ReactNode;
  priceMaps: PriceLookupMaps;
}) {
  // Memoise the wrapper so the Provider value identity only changes when
  // priceMaps does — without this, every parent render allocates a new
  // `{ priceMaps }` object and forces all consumers to re-render even
  // though the underlying maps are stable.
  const value = useMemo(() => ({ priceMaps }), [priceMaps]);
  return (
    <PricingCacheContext.Provider value={value}>
      {children}
    </PricingCacheContext.Provider>
  );
}

const EMPTY_PRICE_MAPS: PriceLookupMaps = {
  partnerPrices: new Map(),
  groupPrices: new Map(),
  productPrices: new Map(),
  partnerGroups: new Map(),
  userPartnerPrices: new Map(),
  userGroupPrices: new Map(),
};

export function usePricingCache(): PricingCacheContextType {
  const context = useContext(PricingCacheContext);
  if (!context) return { priceMaps: EMPTY_PRICE_MAPS };
  return context;
}
